/**
 * Confirm clearing the Agent Review queue for the current task type.
 */

import { useState } from "react";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import { agentReviewAPI, type ClearQueueResult } from "@/services/api/agentReview";

export interface ClearQueueDialogProps {
  open: boolean;
  catalogueKey: string | null;
  sessionId?: string | null;
  taskTypeLabel: string;
  outstandingCount: number;
  reviewedCount: number;
  onClose: () => void;
  onCleared: (result: ClearQueueResult) => void;
}

export function ClearQueueDialog({
  open,
  catalogueKey,
  sessionId,
  taskTypeLabel,
  outstandingCount,
  reviewedCount,
  onClose,
  onCleared,
}: ClearQueueDialogProps) {
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClear = async () => {
    if (!catalogueKey) return;
    setClearing(true);
    setError(null);
    try {
      const result = await agentReviewAPI.clearQueue(catalogueKey, sessionId);
      onCleared(result);
      onClose();
    } catch (err: unknown) {
      const status =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 404) {
        setError("Clear endpoint not found — restart the API server and try again.");
      } else {
        setError("Couldn't clear the queue. Try again.");
      }
    } finally {
      setClearing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Clear to review</DialogTitle>
      <DialogBody className="space-y-3">
        <p className="text-sm text-slate-300 leading-relaxed">
          Remove <span className="text-slate-100">{outstandingCount}</span> unreviewed case
          {outstandingCount === 1 ? "" : "s"} from{" "}
          <span className="text-slate-100">To review</span> for{" "}
          <span className="text-slate-100">{taskTypeLabel}</span>?
        </p>
        <p className="text-sm text-slate-400 leading-relaxed">
          Imported live cases and practice samples that you have not reviewed yet will be removed.
          {reviewedCount > 0 ? (
            <>
              {" "}
              Your <span className="text-slate-300">{reviewedCount} reviewed</span> case
              {reviewedCount === 1 ? "" : "s"} and submitted ratings are kept.
            </>
          ) : null}{" "}
          Production agent runs are not affected — use Import tasks to load a fresh set.
        </p>
        {error ? <p className="text-sm text-rose-400">{error}</p> : null}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={clearing}>
          Cancel
        </Button>
        <Button color="red" onClick={() => void handleClear()} disabled={clearing || !catalogueKey}>
          {clearing ? "Clearing…" : "Clear to review"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
